import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { API, formatCurrency, fmtErr, inr } from "../lib/api";
import { InvoiceForm } from "../components/InvoiceForm";
import { InvoiceDetail } from "../components/InvoiceDetail";
import { RecordPayment } from "../components/RecordPayment";
import StatusBadge from "../components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";

export function InvoiceList({ type }) {
  const isSale = type === "sale";
  const noteType = isSale ? "credit_note" : "debit_note";
  const [docs, setDocs] = useState([]);
  const [notes, setNotes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("docs");
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const action = searchParams.get("action");

  useEffect(() => {
    setInvoiceDialogOpen(action === "create-invoice");
    setPaymentDialogOpen(action === "payment");
  }, [action]);

  const closeAction = (open, setOpen) => {
    setOpen(open);
    if (!open && searchParams.has("action")) {
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
    }
  };

  const load = useCallback(async () => {
    try {
      const [d, n, p] = await Promise.all([
        API.get(isSale ? "/sales" : "/purchases"),
        API.get(isSale ? "/credit-notes" : "/debit-notes"),
        API.get(`/payments?doc_type=${type}`),
      ]);
      setDocs(d.data);
      setNotes(n.data);
      setPayments(p.data.slice(0, 10));
      setSelected((sel) => (sel ? [...d.data, ...n.data].find((x) => x.id === sel.id) || null : null));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }, [isSale, type]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid={isSale ? "sales-page" : "purchases-page"}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{isSale ? "Sales" : "Purchases"}</h1>
          <p className="text-sm text-muted-foreground">
            {isSale ? "GST invoices with automatic CGST/SGST/IGST split" : "Purchase bills and input tax credit tracking"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <RecordPayment type={type} onSaved={load} open={paymentDialogOpen} onOpenChange={(value) => closeAction(value, setPaymentDialogOpen)} />
          <InvoiceForm type={noteType} onSaved={load} />
          <InvoiceForm type={type} onSaved={load} open={invoiceDialogOpen} onOpenChange={(value) => closeAction(value, setInvoiceDialogOpen)} />
        </div>
      </div>

      <div className="flex gap-2" data-testid="doc-tabs">
        <Button variant={tab === "docs" ? "default" : "outline"} size="sm" onClick={() => setTab("docs")} data-testid="tab-docs">
          {isSale ? "Invoices" : "Bills"}
        </Button>
        <Button variant={tab === "notes" ? "default" : "outline"} size="sm" onClick={() => setTab("notes")} data-testid="tab-notes">
          {isSale ? "Credit Notes" : "Debit Notes"}
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        {loading ? (
          <p className="p-10 text-center text-sm text-muted-foreground">Loading…</p>
        ) : tab === "notes" ? (
          notes.length === 0 ? (
            <div className="p-12 text-center" data-testid="notes-empty-state">
              <p className="font-heading text-lg font-semibold">No {isSale ? "credit" : "debit"} notes yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {isSale
                  ? "Issue a credit note for sales returns, rate differences or post-sale discounts."
                  : "Record a debit note for purchase returns or rate differences with a vendor."}
              </p>
            </div>
          ) : (
            <Table data-testid={isSale ? "credit-notes-table" : "debit-notes-table"}>
              <TableHeader>
                <TableRow>
                  <TableHead>No.</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>{isSale ? "Customer" : "Vendor"}</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead className="text-right">Taxable</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notes.map((d) => (
                  <TableRow
                    key={d.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => setSelected(d)}
                    data-testid={`note-row-${d.number}`}
                  >
                    <TableCell className="font-medium">{d.number}</TableCell>
                    <TableCell>{d.date}</TableCell>
                    <TableCell>{d.party_name}</TableCell>
                    <TableCell>{d.reason || "—"}</TableCell>
                    <TableCell>{d.ref_number || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(d.taxable / (Number(d.exchange_rate) || 1), d.currency || "INR")}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency((d.cgst + d.sgst + d.igst) / (Number(d.exchange_rate) || 1), d.currency || "INR")}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{formatCurrency(d.total / (Number(d.exchange_rate) || 1), d.currency || "INR")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        ) : docs.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <p className="font-heading text-lg font-semibold">No {isSale ? "invoices" : "purchase bills"} yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {isSale
                ? "Create your first GST invoice — stock, ledgers and reports update automatically."
                : "Record a purchase bill to track inventory in and input tax credit."}
            </p>
          </div>
        ) : (
          <Table data-testid={isSale ? "sales-table" : "purchases-table"}>
            <TableHeader>
              <TableRow>
                <TableHead>No.</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>{isSale ? "Customer" : "Vendor"}</TableHead>
                <TableHead className="text-right">Taxable</TableHead>
                <TableHead className="text-right">GST</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Due</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map((d) => (
                <TableRow
                  key={d.id}
                  className="cursor-pointer transition-colors hover:bg-muted/50"
                  onClick={() => setSelected(d)}
                  data-testid={`invoice-row-${d.number}`}
                >
                  <TableCell className="font-medium">{d.number}</TableCell>
                  <TableCell>{d.date}</TableCell>
                  <TableCell>{d.party_name}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(d.taxable / (Number(d.exchange_rate) || 1), d.currency || "INR")}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency((d.cgst + d.sgst + d.igst) / (Number(d.exchange_rate) || 1), d.currency || "INR")}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{formatCurrency(d.total / (Number(d.exchange_rate) || 1), d.currency || "INR")}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency((d.total - (d.tds_amount || 0) - d.paid_amount) / (Number(d.exchange_rate) || 1), d.currency || "INR")}</TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <InvoiceDetail doc={selected} onClose={() => setSelected(null)} onChanged={load} />

      {payments.length > 0 && (
        <div className="rounded-lg border bg-card" data-testid="recent-payments">
          <div className="border-b p-4">
            <h2 className="font-heading text-base font-semibold tracking-tight">
              Recent {isSale ? "Receipts" : "Payments"}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="payments-table" className="min-w-[560px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>{isSale ? "Customer" : "Vendor"}</TableHead>
                  <TableHead>Against</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                    <TableCell>{p.date}</TableCell>
                    <TableCell>{p.party_name}</TableCell>
                    <TableCell className="font-medium">{p.doc_number}</TableCell>
                    <TableCell>{p.mode}</TableCell>
                    <TableCell className={`text-right font-medium tabular-nums ${isSale ? "text-[#008A52]" : "text-[#E02424]"}`}>
                      {isSale ? "+" : "−"}{inr(p.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Sales() {
  return <InvoiceList type="sale" />;
}
